/**
 * Admin Dashboard — sealed to `role === "admin"` users only.
 *
 * Server-side: every endpoint goes through `Depends(require_admin)` in
 * `routes/admin.py`, which 403s any non-admin (no matter what the UI says).
 *
 * Client-side: this page checks `user.role` on mount and redirects regular
 * users home. The sidebar also hides the nav entry. That's three layers
 * of "you cannot see this".
 *
 * Layout: 4-tab single-page view (Overview · Tickets · Users · Health).
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ShieldCheck, Users, TicketIcon, Activity, LayoutDashboard, RefreshCw,
  CheckCircle2, AlertTriangle, Send, X, Mail, Database, Sparkles, AlertOctagon,
  UserPlus, Trash2, Check, Inbox, MessageSquarePlus, Bug, Lightbulb, Heart, MessageCircle,
  MousePointerClick, AtSign, Eye, MailWarning, MailX, Clock
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "../components/ui/sonner";
import { useConfirm } from "../components/ConfirmDialog";

const TABS = [
  { key: "overview", label: "Overview", icon: LayoutDashboard },
  { key: "invites", label: "Invites", icon: UserPlus },
  { key: "feedback", label: "Feedback", icon: MessageSquarePlus },
  { key: "tickets", label: "Tickets", icon: TicketIcon },
  { key: "users", label: "Users", icon: Users },
  { key: "emails", label: "Emails", icon: Mail },
  { key: "health", label: "Health", icon: Activity },
];

export default function AdminDashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  // Client-side gate (server is the real gate; this just prevents a flash)
  useEffect(() => {
    if (user && user.role !== "admin") {
      toast.error("Admin access required");
      navigate("/portfolio", { replace: true });
    }
  }, [user, navigate]);

  if (!user || user.role !== "admin") return null;

  return (
    <div data-testid="admin-dashboard-page" className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8 flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <ShieldCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
            <p className="text-[10px] tracking-[0.25em] uppercase text-[#D4AF37]">Admin Only</p>
          </div>
          <h1 className="font-serif text-3xl md:text-4xl text-white tracking-tight">Operations Console</h1>
          <p className="text-white/60 text-sm mt-2 max-w-xl">
            Live state of the EQUIA platform — tickets, users, sub-systems, and recent errors.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-white/[0.10] mb-6 flex items-center gap-1 overflow-x-auto">
        {TABS.map(({ key, label, icon: Icon }) => {
          const active = tab === key;
          return (
            <button
              key={key}
              type="button"
              data-testid={`admin-tab-${key}`}
              onClick={() => setTab(key)}
              className={`px-4 py-2.5 inline-flex items-center gap-2 text-xs tracking-wider uppercase border-b-2 transition-colors ${
                active
                  ? "text-[#D4AF37] border-[#D4AF37]"
                  : "text-white/60 hover:text-white border-transparent"
              }`}
            >
              <Icon className="w-3.5 h-3.5" strokeWidth={1.6} />
              {label}
            </button>
          );
        })}
      </div>

      {tab === "overview" && <OverviewTab />}
      {tab === "invites" && <InvitesTab />}
      {tab === "feedback" && <FeedbackTab />}
      {tab === "tickets" && <TicketsTab />}
      {tab === "users" && <UsersTab />}
      {tab === "emails" && <EmailsTab />}
      {tab === "health" && <HealthTab />}
    </div>
  );
}

// =============================================================================
// Overview
// =============================================================================

function OverviewTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/overview");
      setData(data);
    } catch (e) {
      toast.error("Failed to load overview");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !data) {
    return <div className="text-white/60 text-sm py-12 text-center">Loading…</div>;
  }

  const maxSignups = Math.max(1, ...(data.signups_7d || []).map((d) => d.count));

  return (
    <div className="space-y-6">
      {/* Top stat row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Users" value={data.users.total} sub={`${data.users.signups_today} new today`} />
        <StatCard label="Active · 7 days" value={data.users.active_7d} sub={`${data.users.active_24h} in 24h`} />
        <StatCard label="Active Assets" value={data.portfolio.active_assets} sub={`${data.portfolio.deal_drafts} deal drafts`} />
        <StatCard
          label="Open Tickets"
          value={data.support.open_tickets}
          sub={`${data.support.total_tickets} total`}
          highlight={data.support.open_tickets > 0}
        />
      </div>

      {/* Second row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Documents" value={data.portfolio.documents} />
        <StatCard label="Residencies" value={data.portfolio.residencies} />
        <StatCard label="Deletion Pending" value={data.users.deletion_pending} highlight={data.users.deletion_pending > 0} />
        <StatCard label="Signups · Today" value={data.users.signups_today} />
      </div>

      {/* Signup trend */}
      <div className="bg-white/[0.02] border border-white/[0.10] p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="text-[10px] tracking-[0.2em] uppercase text-white/60">Signups · Last 7 Days</p>
          <button
            onClick={load}
            data-testid="admin-overview-refresh"
            className="text-white/65 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase"
          >
            <RefreshCw className="w-3 h-3" /> Refresh
          </button>
        </div>
        <div className="flex items-end gap-2 h-32">
          {(data.signups_7d || []).map((d, i) => (
            <div key={i} className="flex-1 flex flex-col items-center gap-2">
              <div
                className="w-full bg-[#D4AF37]/40 hover:bg-[#D4AF37] transition-colors"
                style={{ height: `${(d.count / maxSignups) * 100}%`, minHeight: "2px" }}
                title={`${d.date}: ${d.count}`}
              />
              <span className="text-[9px] text-white/65 tabular-nums">{d.date.slice(5)}</span>
              <span className="text-[10px] text-white/85 tabular-nums">{d.count}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Tickets
// =============================================================================

function TicketsTab() {
  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = filter !== "all" ? { status: filter } : {};
      const { data } = await api.get("/admin/tickets", { params });
      setTickets(data.tickets || []);
    } catch (e) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        {["open", "closed", "all"].map((s) => (
          <button
            key={s}
            type="button"
            data-testid={`admin-ticket-filter-${s}`}
            onClick={() => setFilter(s)}
            className={`px-3 py-1.5 text-[10px] tracking-wider uppercase border transition-colors ${
              filter === s
                ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/[0.04]"
                : "border-white/[0.12] text-white/60 hover:text-white"
            }`}
          >
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-white/60 text-sm py-12 text-center">Loading…</div>
      ) : tickets.length === 0 ? (
        <div className="text-white/65 text-sm py-12 text-center border border-white/[0.10]">No tickets in this filter.</div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setActive(t)}
              data-testid={`admin-ticket-row-${t.id}`}
              className="w-full text-left bg-white/[0.02] hover:bg-white/[0.04] border border-white/[0.10] hover:border-[#D4AF37]/20 p-4 transition-colors"
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[9px] tracking-wider uppercase px-1.5 py-0.5 ${
                      t.status === "open" ? "bg-amber-500/10 text-amber-300" : "bg-emerald-500/10 text-emerald-300"
                    }`}>{t.status}</span>
                    <span className="text-[9px] tracking-wider uppercase text-[#D4AF37]/70">{t.category}</span>
                  </div>
                  <p className="text-white text-sm font-medium">{t.subject}</p>
                  <p className="text-white/60 text-xs mt-0.5 truncate">{t.user_email} · {t.user_name || "—"}</p>
                </div>
                <div className="text-right">
                  <p className="text-white/60 text-[10px] tabular-nums">{(t.created_at || "").slice(0, 16).replace("T", " ")}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {active && <TicketDetailModal ticket={active} onClose={() => setActive(null)} onChanged={() => { setActive(null); load(); }} />}
    </div>
  );
}

function TicketDetailModal({ ticket, onClose, onChanged }) {
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const sendReply = async () => {
    if (!reply.trim()) {
      toast.error("Reply cannot be empty");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post(`/admin/tickets/${ticket.id}/reply`, { reply });
      if (data.email_status === "sent") {
        toast.success("Reply sent + ticket closed");
      } else {
        toast.success(`Ticket closed (email: ${data.email_status})`);
      }
      onChanged();
    } catch (e) {
      toast.error("Failed to send reply");
    } finally {
      setSending(false);
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/admin/tickets/${ticket.id}/reopen`);
      toast.success("Ticket reopened");
      onChanged();
    } catch (e) {
      toast.error("Failed");
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/80 z-50" onClick={onClose} />
      <motion.div
        initial={{ opacity: 0, scale: 0.96 }}
        animate={{ opacity: 1, scale: 1 }}
        className="fixed inset-4 md:inset-12 z-50 bg-[#0A0A0A] border border-white/[0.12] overflow-auto flex flex-col"
        data-testid="admin-ticket-modal"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/[0.10] sticky top-0 bg-[#0A0A0A] z-10">
          <div className="min-w-0">
            <p className="text-[10px] tracking-[0.15em] uppercase text-[#D4AF37]">Ticket · {ticket.category}</p>
            <h3 className="font-serif text-lg text-white truncate">{ticket.subject}</h3>
          </div>
          <button onClick={onClose} className="text-white/60 hover:text-white p-2">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {/* Sender */}
          <div className="bg-white/[0.02] border border-white/[0.10] p-3 grid grid-cols-3 gap-3">
            <div>
              <p className="text-[9px] uppercase tracking-wider text-white/60 mb-0.5">From</p>
              <p className="text-white text-xs">{ticket.user_name || "—"}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-white/60 mb-0.5">Email</p>
              <a href={`mailto:${ticket.user_email}`} className="text-[#D4AF37] hover:text-[#E8C757] text-xs">{ticket.user_email}</a>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-wider text-white/60 mb-0.5">Submitted</p>
              <p className="text-white text-xs">{(ticket.created_at || "").slice(0, 16).replace("T", " ")}</p>
            </div>
          </div>

          {/* Description */}
          <div>
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Description</p>
            <div className="bg-white/[0.02] border border-white/[0.10] p-4 text-white/80 text-sm whitespace-pre-wrap leading-relaxed">
              {ticket.description}
            </div>
          </div>

          {/* Transcript */}
          {Array.isArray(ticket.transcript) && ticket.transcript.length > 0 && (
            <div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Chat Transcript ({ticket.transcript.length})</p>
              <div className="bg-white/[0.02] border border-white/[0.10] p-4 space-y-2 max-h-[300px] overflow-auto">
                {ticket.transcript.map((m, i) => (
                  <div key={i} className="flex gap-3 text-xs">
                    <span className={`uppercase tracking-wider text-[9px] mt-0.5 ${m.role === "user" ? "text-[#D4AF37]" : "text-white/60"}`} style={{ minWidth: "60px" }}>
                      {m.role}
                    </span>
                    <p className="text-white/80 whitespace-pre-wrap flex-1">{m.content}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Previous reply */}
          {ticket.reply_text && (
            <div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-emerald-300 mb-2">Replied · {(ticket.replied_at || "").slice(0, 16).replace("T", " ")}</p>
              <div className="bg-emerald-500/[0.03] border border-emerald-500/15 p-4 text-white/85 text-sm whitespace-pre-wrap leading-relaxed">
                {ticket.reply_text}
              </div>
            </div>
          )}

          {/* Reply form */}
          {ticket.status === "open" ? (
            <div>
              <p className="text-[10px] tracking-[0.15em] uppercase text-white/60 mb-2">Your Reply</p>
              <textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                rows={6}
                data-testid="admin-ticket-reply-textarea"
                placeholder="Write your response. The user will receive this via email and the ticket will close."
                className="w-full bg-[#0A0A0A] border border-white/[0.12] focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2.5 outline-none resize-y leading-relaxed"
              />
              <div className="flex items-center justify-between gap-3 mt-3">
                <p className="text-white/65 text-[10px]">
                  Sent via Resend from <span className="text-white/65">support@getequia.com</span>
                </p>
                <button
                  type="button"
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                  data-testid="admin-ticket-reply-send"
                  className="bg-[#D4AF37] hover:bg-[#E8C757] disabled:bg-white/10 disabled:text-white/65 disabled:cursor-not-allowed text-black text-xs font-semibold tracking-[0.15em] uppercase px-5 py-2.5 inline-flex items-center gap-2 transition-colors"
                >
                  <Send className="w-3.5 h-3.5" />
                  {sending ? "Sending…" : "Send Reply + Close"}
                </button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between gap-3 bg-emerald-500/[0.03] border border-emerald-500/15 p-3">
              <p className="text-emerald-200/80 text-xs">
                <CheckCircle2 className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />
                Ticket closed. You can reopen if needed.
              </p>
              <button
                type="button"
                onClick={reopen}
                className="text-white/85 hover:text-white text-[10px] tracking-wider uppercase border border-white/10 hover:border-white/30 px-3 py-1.5 transition-colors"
              >
                Reopen
              </button>
            </div>
          )}
        </div>
      </motion.div>
    </>
  );
}

// =============================================================================
// Users
// =============================================================================

function UsersTab() {
  const { user: me } = useAuth();
  const confirm = useConfirm();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = async (q = "") => {
    setLoading(true);
    try {
      const params = q ? { search: q } : {};
      const { data } = await api.get("/admin/users", { params });
      setUsers(data.users || []);
    } catch (e) {
      toast.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const remove = async (u) => {
    const body =
      `  ${u.email}\n` +
      (u.asset_count > 0 ? `\n⚠️ This user has ${u.asset_count} asset(s) — all will also be deleted.\n` : "") +
      `\nThis cannot be undone.`;
    const ok = await confirm({
      title: "Permanently delete this user?",
      body,
      confirmText: "Delete",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(u.id);
    try {
      const r = await api.delete(`/admin/users/${u.id}`);
      const cascade = r.data?.cascade || {};
      const detail = Object.keys(cascade).length
        ? ` · cleaned ${Object.entries(cascade).map(([k, v]) => `${v} ${k}`).join(", ")}`
        : "";
      toast.success(`Deleted ${u.email}${detail}`);
      await load(search);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 max-w-xl">
        <input
          type="text"
          placeholder="Search by email…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(search)}
          data-testid="admin-users-search"
          className="flex-1 bg-[#0A0A0A] border border-white/[0.12] focus:border-[#D4AF37]/40 text-white text-sm px-3 py-2 outline-none"
        />
        <button
          type="button"
          onClick={() => load(search)}
          className="bg-white/[0.04] hover:bg-white/[0.08] border border-white/[0.12] text-white text-xs uppercase tracking-wider px-4 py-2 transition-colors"
        >
          Search
        </button>
        <button
          type="button"
          onClick={async () => {
            try {
              const res = await api.get("/admin/users/export.csv", { responseType: "blob" });
              const blob = new Blob([res.data], { type: "text/csv;charset=utf-8" });
              const url = window.URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              const stamp = new Date().toISOString().replace(/[-:]/g, "").split(".")[0];
              a.download = `equia-users-${stamp}.csv`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              window.URL.revokeObjectURL(url);
            } catch {
              // ignore; admin can retry
            }
          }}
          data-testid="admin-users-export-csv"
          className="bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/30 text-[#D4AF37] text-xs uppercase tracking-wider px-4 py-2 transition-colors"
        >
          Export emails (CSV)
        </button>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm py-12 text-center">Loading…</div>
      ) : (
        <div className="border border-white/[0.10] overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.02] border-b border-white/[0.10]">
              <tr className="text-[10px] uppercase tracking-wider text-white/60">
                <th className="text-left px-3 py-2 font-normal">Email</th>
                <th className="text-left px-3 py-2 font-normal">Name</th>
                <th className="text-left px-3 py-2 font-normal">Role</th>
                <th className="text-right px-3 py-2 font-normal">Assets</th>
                <th className="text-left px-3 py-2 font-normal">Signed Up</th>
                <th className="text-left px-3 py-2 font-normal">Last Login</th>
                <th className="text-left px-3 py-2 font-normal">Newsletter</th>
                <th className="text-left px-3 py-2 font-normal">Status</th>
                <th className="text-right px-3 py-2 font-normal w-12"></th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = me?.id === u.id || me?.email === u.email;
                return (
                  <tr key={u.id} className="border-b border-white/[0.08] last:border-0 hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.id}`}>
                    <td className="px-3 py-2.5 text-white">{u.email}</td>
                    <td className="px-3 py-2.5 text-white/90">{u.name || "—"}</td>
                    <td className="px-3 py-2.5">
                      <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 ${u.role === "admin" ? "bg-[#D4AF37]/10 text-[#D4AF37]" : "bg-white/5 text-white/85"}`}>
                        {u.role || "user"}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right text-white tabular-nums">{u.asset_count}</td>
                    <td className="px-3 py-2.5 text-white/65 tabular-nums">{(u.created_at || "").slice(0, 10)}</td>
                    <td className="px-3 py-2.5 text-white/65 tabular-nums">{(u.last_login_at || "—").slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5">
                      {u.marketing_consent ? (
                        <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 bg-[#D4AF37]/10 text-[#D4AF37]">Opted in</span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-wider text-white/65">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      {u.deletion_scheduled_at ? (
                        <span className="text-red-300 text-[10px] uppercase tracking-wider">Deletion pending</span>
                      ) : u.has_accepted_legal ? (
                        <span className="text-emerald-300/70 text-[10px] uppercase tracking-wider">Active</span>
                      ) : (
                        <span className="text-amber-300/70 text-[10px] uppercase tracking-wider">Legal pending</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {!isSelf ? (
                        <button
                          type="button"
                          onClick={() => remove(u)}
                          disabled={busyId === u.id}
                          data-testid={`admin-user-delete-${u.id}`}
                          title={`Delete ${u.email}`}
                          className="text-white/65 hover:text-red-300 hover:bg-red-500/[0.06] p-1.5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      ) : (
                        <span className="text-white/15 text-[10px]" title="Cannot delete your own account">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Invites — pending requests from the landing page
// =============================================================================

function InvitesTab() {
  const confirm = useConfirm();
  const [data, setData] = useState({ requests: [], counts: { pending: 0, accepted: 0, total: 0 } });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [filter, setFilter] = useState("pending"); // pending | accepted | all

  const load = async () => {
    setLoading(true);
    try {
      const q = filter === "all" ? "" : `?status=${filter}`;
      const r = await api.get(`/admin/invite-requests${q}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load invite requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter]);

  const accept = async (req) => {
    const ok = await confirm({
      title: `Approve ${req.email}?`,
      body: "A welcome email with a magic link to set their password will be sent immediately.",
      confirmText: "Approve & Send",
      cancelText: "Cancel",
    });
    if (!ok) return;
    setBusyId(req.id);
    try {
      const r = await api.post(`/admin/invite-requests/${req.id}/accept`);
      if (r.data.email_status === "sent") {
        toast.success(`Accepted · invite email sent to ${req.email}`);
      } else if (r.data.email_status === "skipped_no_key") {
        toast.warning("Accepted but email service is not configured");
      } else {
        toast.warning(`Accepted but email ${r.data.email_status}`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Accept failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (req) => {
    const ok = await confirm({
      title: "Delete invite request?",
      body: `From ${req.email}.\n\nThis cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(req.id);
    try {
      await api.delete(`/admin/invite-requests/${req.id}`);
      toast.success("Request deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-invites-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-5 text-[11px] tracking-wider uppercase">
          <span className="text-white/65">
            Pending <span className="text-[#D4AF37] font-mono ml-1.5">{data.counts.pending}</span>
          </span>
          <span className="text-white/65">
            Accepted <span className="text-white/90 font-mono ml-1.5">{data.counts.accepted}</span>
          </span>
          <span className="text-white/65">
            Total <span className="text-white/65 font-mono ml-1.5">{data.counts.total}</span>
          </span>
        </div>
        <div className="flex items-center gap-1 bg-white/[0.02] border border-white/[0.10] p-0.5">
          {[
            { v: "pending", l: "Pending" },
            { v: "accepted", l: "Accepted" },
            { v: "all", l: "All" },
          ].map((o) => (
            <button
              key={o.v}
              onClick={() => setFilter(o.v)}
              data-testid={`admin-invites-filter-${o.v}`}
              className={`px-3 py-1.5 text-[10px] tracking-wider uppercase transition-colors ${
                filter === o.v ? "bg-[#D4AF37]/[0.08] text-[#D4AF37]" : "text-white/60 hover:text-white"
              }`}
            >
              {o.l}
            </button>
          ))}
          <button
            onClick={load}
            data-testid="admin-invites-refresh"
            className="px-3 py-1.5 text-white/65 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase"
            title="Refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? "animate-spin" : ""}`} />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm py-12 text-center">Loading…</div>
      ) : data.requests.length === 0 ? (
        <div className="border border-white/[0.10] bg-white/[0.01] p-12 text-center" data-testid="admin-invites-empty">
          <Inbox className="w-6 h-6 text-white/55 mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-white/65 text-sm font-sans">
            No {filter === "all" ? "" : filter} invite requests.
          </p>
          <p className="text-white/60 text-xs font-sans mt-1">
            New requests submitted from the landing page will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.requests.map((r) => (
            <InviteRow
              key={r.id}
              req={r}
              busy={busyId === r.id}
              onAccept={() => accept(r)}
              onDelete={() => remove(r)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function InviteRow({ req, busy, onAccept, onDelete }) {
  const accepted = req.status === "accepted";
  const created = req.created_at ? req.created_at.slice(0, 10) : "—";
  const acceptedAt = req.accepted_at ? req.accepted_at.slice(0, 16).replace("T", " ") : null;

  return (
    <div
      className={`border p-4 md:p-5 transition-colors ${
        accepted
          ? "border-white/[0.09] bg-white/[0.005]"
          : "border-white/[0.12] bg-white/[0.02] hover:border-[#D4AF37]/20"
      }`}
      data-testid={`admin-invite-row-${req.id}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1.5 flex-wrap">
            <p className="text-white text-sm font-medium truncate">{req.name}</p>
            <span className={`text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 ${
              accepted
                ? "text-emerald-300/70 bg-emerald-500/[0.08]"
                : "text-[#D4AF37] bg-[#D4AF37]/[0.08]"
            }`}>
              {accepted ? "Accepted" : "Pending"}
            </span>
          </div>
          <a
            href={`mailto:${req.email}`}
            className="text-white/80 text-xs font-mono hover:text-[#D4AF37] transition-colors"
          >
            {req.email}
          </a>
          {req.message && (
            <p className="text-white/60 text-xs leading-relaxed mt-3 font-sans whitespace-pre-wrap break-words max-w-2xl">
              {req.message}
            </p>
          )}
          <p className="text-white/60 text-[10px] mt-2 font-sans">
            Submitted {created}
            {acceptedAt && <> · Accepted {acceptedAt} by {req.accepted_by || "admin"}</>}
          </p>
        </div>

        {!accepted && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onDelete}
              disabled={busy}
              data-testid={`admin-invite-delete-${req.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 text-white/65 hover:text-red-300 hover:bg-red-500/[0.06] border border-white/[0.12] hover:border-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
            <button
              onClick={onAccept}
              disabled={busy}
              data-testid={`admin-invite-accept-${req.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 bg-[#D4AF37] text-black hover:bg-[#B5952F] transition-colors disabled:opacity-40 disabled:cursor-not-allowed font-medium"
            >
              <Check className="w-3 h-3" /> {busy ? "Sending…" : "Accept"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}


// =============================================================================
// Feedback — beta submissions from /feedback
// =============================================================================

const FEEDBACK_CAT_META = {
  bug:        { Icon: Bug,           color: "text-red-300",      bg: "bg-red-500/[0.08]",      label: "Bug" },
  suggestion: { Icon: Lightbulb,     color: "text-[#D4AF37]",    bg: "bg-[#D4AF37]/[0.08]",    label: "Suggestion" },
  praise:     { Icon: Heart,         color: "text-emerald-300",  bg: "bg-emerald-500/[0.08]",  label: "Praise" },
  other:      { Icon: MessageCircle, color: "text-white/90",     bg: "bg-white/[0.06]",        label: "Other" },
};

const FEEDBACK_SEV_META = {
  high:   { color: "text-red-300",      label: "High" },
  medium: { color: "text-amber-300",    label: "Medium" },
  low:    { color: "text-white/60",     label: "Low" },
};

function FeedbackTab() {
  const confirm = useConfirm();
  const [data, setData] = useState({ feedback: [], counts: { open: 0, resolved: 0, total: 0, bugs_open: 0 } });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [categoryFilter, setCategoryFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== "all") params.set("status", statusFilter);
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      const r = await api.get(`/admin/feedback?${params.toString()}`);
      setData(r.data);
    } catch (e) {
      toast.error("Failed to load feedback");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, categoryFilter]);

  const toggleResolved = async (fb) => {
    setBusyId(fb.id);
    try {
      const r = await api.post(`/admin/feedback/${fb.id}/toggle-resolved`);
      toast.success(r.data.resolved ? "Marked as resolved" : "Reopened");
      await load();
    } catch (e) {
      toast.error("Action failed");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (fb) => {
    const ok = await confirm({
      title: "Permanently delete this feedback?",
      body: `"${fb.title}"`,
      confirmText: "Delete",
      cancelText: "Cancel",
      tone: "danger",
    });
    if (!ok) return;
    setBusyId(fb.id);
    try {
      await api.delete(`/admin/feedback/${fb.id}`);
      toast.success("Deleted");
      await load();
    } catch (e) {
      toast.error("Delete failed");
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-feedback-tab">
      {/* Stats + filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-5 text-[11px] tracking-wider uppercase">
          <span className="text-white/65">
            Open <span className="text-[#D4AF37] font-mono ml-1.5">{data.counts.open}</span>
          </span>
          <span className="text-white/65">
            Bugs open <span className="text-red-300 font-mono ml-1.5">{data.counts.bugs_open}</span>
          </span>
          <span className="text-white/65">
            Resolved <span className="text-emerald-300/70 font-mono ml-1.5">{data.counts.resolved}</span>
          </span>
          <span className="text-white/65">
            Total <span className="text-white/65 font-mono ml-1.5">{data.counts.total}</span>
          </span>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            data-testid="admin-feedback-category-filter"
            className="bg-[#0A0A0A] border border-white/[0.12] text-white text-[10px] tracking-wider uppercase px-2.5 py-1.5 focus:border-[#D4AF37] outline-none font-sans"
          >
            <option value="all">All types</option>
            <option value="bug">Bugs</option>
            <option value="suggestion">Suggestions</option>
            <option value="praise">Praise</option>
            <option value="other">Other</option>
          </select>
          <div className="flex items-center gap-1 bg-white/[0.02] border border-white/[0.10] p-0.5">
            {[
              { v: "open", l: "Open" },
              { v: "resolved", l: "Resolved" },
              { v: "all", l: "All" },
            ].map((o) => (
              <button
                key={o.v}
                onClick={() => setStatusFilter(o.v)}
                data-testid={`admin-feedback-status-${o.v}`}
                className={`px-3 py-1.5 text-[10px] tracking-wider uppercase transition-colors ${
                  statusFilter === o.v ? "bg-[#D4AF37]/[0.08] text-[#D4AF37]" : "text-white/60 hover:text-white"
                }`}
              >
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-white/60 text-sm py-12 text-center">Loading…</div>
      ) : data.feedback.length === 0 ? (
        <div className="border border-white/[0.10] bg-white/[0.01] p-12 text-center" data-testid="admin-feedback-empty">
          <MessageSquarePlus className="w-6 h-6 text-white/55 mx-auto mb-3" strokeWidth={1.4} />
          <p className="text-white/65 text-sm font-sans">
            No {statusFilter === "all" ? "" : statusFilter} feedback.
          </p>
          <p className="text-white/60 text-xs font-sans mt-1">
            User submissions from the in-app Feedback page will appear here.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {data.feedback.map((fb) => (
            <FeedbackRow
              key={fb.id}
              fb={fb}
              busy={busyId === fb.id}
              onToggle={() => toggleResolved(fb)}
              onDelete={() => remove(fb)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function FeedbackRow({ fb, busy, onToggle, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const cat = FEEDBACK_CAT_META[fb.category] || FEEDBACK_CAT_META.other;
  const sev = fb.severity ? FEEDBACK_SEV_META[fb.severity] : null;
  const CatIcon = cat.Icon;
  const submitted = fb.created_at ? fb.created_at.slice(0, 16).replace("T", " ") : "—";

  return (
    <div
      className={`border transition-colors ${
        fb.resolved
          ? "border-white/[0.09] bg-white/[0.005] opacity-70"
          : "border-white/[0.12] bg-white/[0.02] hover:border-white/15"
      }`}
      data-testid={`admin-feedback-row-${fb.id}`}
    >
      <div className="p-4 md:p-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={`w-9 h-9 ${cat.bg} flex items-center justify-center flex-shrink-0`}>
              <CatIcon className={`w-4 h-4 ${cat.color}`} strokeWidth={1.5} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className={`text-[9px] tracking-[0.2em] uppercase ${cat.color} font-medium`}>
                  {cat.label}
                </span>
                {sev && (
                  <span className={`text-[9px] tracking-[0.2em] uppercase ${sev.color}`}>
                    · {sev.label}
                  </span>
                )}
                {fb.resolved && (
                  <span className="text-[9px] tracking-[0.2em] uppercase px-1.5 py-0.5 bg-emerald-500/[0.08] text-emerald-300/70">
                    Resolved
                  </span>
                )}
              </div>
              <button
                type="button"
                onClick={() => setExpanded((v) => !v)}
                data-testid={`admin-feedback-expand-${fb.id}`}
                className="text-white text-sm font-medium text-left hover:text-[#D4AF37] transition-colors break-words"
              >
                {fb.title}
              </button>
              <div className="flex items-center gap-3 mt-1.5 text-white/60 text-[10px] font-sans flex-wrap">
                <span>{fb.user_name || fb.user_email}</span>
                <span className="text-white/55">·</span>
                <a href={`mailto:${fb.user_email}`} className="hover:text-[#D4AF37] transition-colors font-mono">
                  {fb.user_email}
                </a>
                <span className="text-white/55">·</span>
                <span>{submitted}</span>
                {fb.page_url && (
                  <>
                    <span className="text-white/55">·</span>
                    <span className="font-mono text-white/65">From {fb.page_url}</span>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={onToggle}
              disabled={busy}
              data-testid={`admin-feedback-toggle-${fb.id}`}
              className={`inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                fb.resolved
                  ? "text-white/65 hover:text-white border-white/[0.12] hover:border-white/20"
                  : "text-emerald-300 hover:bg-emerald-500/[0.08] border-emerald-500/20 hover:border-emerald-500/40 font-medium"
              }`}
            >
              <Check className="w-3 h-3" />
              {fb.resolved ? "Reopen" : "Resolve"}
            </button>
            <button
              onClick={onDelete}
              disabled={busy}
              data-testid={`admin-feedback-delete-${fb.id}`}
              className="inline-flex items-center gap-1.5 text-[11px] tracking-wider uppercase px-3 py-2 text-white/65 hover:text-red-300 hover:bg-red-500/[0.06] border border-white/[0.12] hover:border-red-500/20 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        </div>

        {expanded && (
          <div className="mt-4 pt-4 border-t border-white/[0.10] space-y-3" data-testid={`admin-feedback-detail-${fb.id}`}>
            <p className="text-white/85 text-xs leading-relaxed whitespace-pre-wrap break-words font-sans">
              {fb.description}
            </p>
            {fb.user_agent && (
              <p className="text-white/65 text-[10px] font-mono break-all">
                {fb.user_agent}
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}



// =============================================================================
// Emails — Resend webhook event timeline + bounce-rate at a glance
// =============================================================================

const EVENT_META = {
  "queued":                 { icon: Clock,             color: "text-white/60",    label: "Queued" },
  "email.sent":             { icon: Send,              color: "text-white/80",    label: "Sent" },
  "email.delivered":        { icon: CheckCircle2,      color: "text-emerald-300", label: "Delivered" },
  "email.delivery_delayed": { icon: Clock,             color: "text-amber-300",   label: "Delayed" },
  "email.opened":           { icon: Eye,               color: "text-sky-300",     label: "Opened" },
  "email.clicked":          { icon: MousePointerClick, color: "text-cyan-300",    label: "Clicked" },
  "email.bounced":          { icon: MailX,             color: "text-red-300",     label: "Bounced" },
  "email.complained":       { icon: MailWarning,       color: "text-amber-400",   label: "Complained" },
  "email.failed":           { icon: AlertOctagon,      color: "text-red-400",     label: "Failed" },
  "dispatch_failed":        { icon: AlertOctagon,      color: "text-red-400",     label: "Dispatch failed" },
};

function eventChip(et) {
  const m = EVENT_META[et] || { icon: AtSign, color: "text-white/60", label: et };
  const Icon = m.icon;
  return (
    <span className={`inline-flex items-center gap-1 ${m.color} text-[11px]`}>
      <Icon className="w-3 h-3" strokeWidth={1.6} />
      {m.label}
    </span>
  );
}

function EmailsTab() {
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ event_type: "", purpose: "", recipient: "" });
  const [selectedEmailId, setSelectedEmailId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.event_type) params.set("event_type", filters.event_type);
      if (filters.purpose)    params.set("purpose", filters.purpose);
      if (filters.recipient)  params.set("recipient", filters.recipient);
      params.set("limit", "100");
      const [statsRes, listRes] = await Promise.all([
        api.get("/admin/email-events/stats?days=7"),
        api.get(`/admin/email-events?${params.toString()}`),
      ]);
      setStats(statsRes.data);
      setRows(listRes.data.events || []);
    } catch (e) {
      toast.error("Failed to load email events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  if (loading && !stats) {
    return <div data-testid="admin-emails-loading" className="text-white/60 text-sm py-12 text-center">Loading…</div>;
  }

  return (
    <div data-testid="admin-emails-tab" className="space-y-6">
      {/* Headline rates */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <StatCard
            label="Bounce rate · 7d"
            value={`${stats.rates.bounce_rate.toFixed(1)}%`}
            sub={`${stats.headline_counts.bounced} / ${stats.headline_counts.sent} sent`}
            highlight={stats.rates.bounce_rate > 2}
          />
          <StatCard
            label="Open rate · 7d"
            value={`${stats.rates.open_rate.toFixed(1)}%`}
            sub={`${stats.headline_counts.opened} opens`}
          />
          <StatCard
            label="Click rate · 7d"
            value={`${stats.rates.click_rate.toFixed(1)}%`}
            sub={`${stats.headline_counts.clicked} clicks`}
          />
          <StatCard
            label="Dispatch failures · 7d"
            value={stats.headline_counts.dispatch_failed + stats.headline_counts.failed}
            sub="Resend rejected or 5xx"
            highlight={(stats.headline_counts.dispatch_failed + stats.headline_counts.failed) > 0}
          />
        </div>
      )}

      {stats && <EmailDailyChart perDay={stats.per_day} days={stats.days} />}

      {/* Filters */}
      <div className="bg-[#0A0A0A] border border-white/[0.10] p-4 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-[10px] tracking-[0.15em] uppercase text-white/45">Event</label>
          <select
            data-testid="email-filter-event"
            value={filters.event_type}
            onChange={e => setFilters(f => ({ ...f, event_type: e.target.value }))}
            className="bg-black border border-white/15 text-white text-xs px-2.5 py-2 min-w-[160px] focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value="">All events</option>
            <option value="queued">Queued</option>
            <option value="email.sent">Sent</option>
            <option value="email.delivered">Delivered</option>
            <option value="email.delivery_delayed">Delayed</option>
            <option value="email.opened">Opened</option>
            <option value="email.clicked">Clicked</option>
            <option value="email.bounced">Bounced</option>
            <option value="email.complained">Complained</option>
            <option value="email.failed">Failed</option>
            <option value="dispatch_failed">Dispatch failed</option>
          </select>
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-[10px] tracking-[0.15em] uppercase text-white/45">Purpose</label>
          <select
            data-testid="email-filter-purpose"
            value={filters.purpose}
            onChange={e => setFilters(f => ({ ...f, purpose: e.target.value }))}
            className="bg-black border border-white/15 text-white text-xs px-2.5 py-2 min-w-[160px] focus:outline-none focus:border-[#D4AF37]/60"
          >
            <option value="">All purposes</option>
            <option value="password_reset">Password reset</option>
            <option value="invite">Beta invite</option>
            <option value="payment_reminder">Payment reminder</option>
            <option value="visa_reminder">Visa reminder</option>
            <option value="custom_alert">Custom alert</option>
            <option value="account_exists">Account exists</option>
            <option value="deletion_scheduled">Deletion scheduled</option>
            <option value="deletion_final">Deletion final</option>
            <option value="support_inbound">Support inbound</option>
            <option value="support_reply">Support reply</option>
          </select>
        </div>
        <div className="flex flex-col gap-1 flex-1 min-w-[200px]">
          <label className="text-[10px] tracking-[0.15em] uppercase text-white/45">Recipient (partial match)</label>
          <input
            data-testid="email-filter-recipient"
            value={filters.recipient}
            onChange={e => setFilters(f => ({ ...f, recipient: e.target.value }))}
            placeholder="alice@example.com or @gmail.com"
            className="bg-black border border-white/15 text-white text-xs px-2.5 py-2 placeholder:text-white/30 focus:outline-none focus:border-[#D4AF37]/60"
          />
        </div>
        <button
          data-testid="email-filter-apply"
          type="button"
          onClick={load}
          className="bg-[#D4AF37] text-black text-[11px] tracking-wider uppercase px-4 py-2 hover:bg-[#D4AF37]/90"
        >Apply</button>
      </div>

      {/* Table */}
      <div className="bg-[#0A0A0A] border border-white/[0.10]">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/[0.08] text-[10px] tracking-[0.15em] uppercase text-white/50">
          <div className="col-span-3">When</div>
          <div className="col-span-2">Event</div>
          <div className="col-span-2">Purpose</div>
          <div className="col-span-3">Recipient</div>
          <div className="col-span-2">Subject</div>
        </div>
        {rows.length === 0 ? (
          <div className="px-4 py-12 text-center text-white/45 text-sm" data-testid="email-events-empty">
            No events match those filters.
          </div>
        ) : rows.map(r => (
          <button
            key={r.id}
            data-testid={`email-row-${r.id}`}
            type="button"
            onClick={() => r.email_id && setSelectedEmailId(r.email_id)}
            disabled={!r.email_id}
            className={`w-full grid grid-cols-12 gap-3 px-4 py-3 border-b border-white/[0.05] text-xs items-center text-left
              ${r.email_id ? "hover:bg-white/[0.02] cursor-pointer" : "cursor-default"}`}
          >
            <div className="col-span-3 text-white/60 font-mono text-[11px]">
              {new Date(r.created_at).toLocaleString()}
            </div>
            <div className="col-span-2">{eventChip(r.event_type)}</div>
            <div className="col-span-2 text-white/55">{r.purpose || <span className="text-white/30">—</span>}</div>
            <div className="col-span-3 text-white/80 truncate">{(r.to || []).join(", ")}</div>
            <div className="col-span-2 text-white/55 truncate" title={r.subject || ""}>{r.subject || <span className="text-white/30">—</span>}</div>
          </button>
        ))}
      </div>

      {selectedEmailId && (
        <EmailTimelineModal emailId={selectedEmailId} onClose={() => setSelectedEmailId(null)} />
      )}
    </div>
  );
}

function EmailDailyChart({ perDay, days }) {
  // Build a list of last `days` dates in order, even if empty, so the chart
  // doesn't collapse to gaps on slow days.
  const dates = [];
  const today = new Date();
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    dates.push(d.toISOString().slice(0, 10));
  }
  const totals = dates.map(d => {
    const e = perDay[d] || {};
    return Object.values(e).reduce((s, n) => s + n, 0);
  });
  const max = Math.max(1, ...totals);

  return (
    <div className="bg-[#0A0A0A] border border-white/[0.10] p-5" data-testid="email-daily-chart">
      <p className="text-[10px] tracking-[0.15em] uppercase text-white/45 mb-4">Events per day · last {days}d</p>
      <div className="flex items-end gap-1.5 h-32">
        {dates.map((d, i) => {
          const e = perDay[d] || {};
          const sent   = (e.sent || 0) + (e.delivered || 0);
          const open   = e.opened || 0;
          const click  = e.clicked || 0;
          const bounce = (e.bounced || 0) + (e.complained || 0) + (e.failed || 0) + (e.dispatch_failed || 0);
          const h = (n) => `${(n / max) * 100}%`;
          return (
            <div key={d} className="flex-1 flex flex-col items-center gap-1 group min-w-0">
              <div className="w-full flex flex-col-reverse h-28 bg-white/[0.02]">
                <div style={{ height: h(sent) }}   className="bg-emerald-400/40 transition-all" title={`Delivered ${sent}`} />
                <div style={{ height: h(open) }}   className="bg-sky-400/40 transition-all"     title={`Opened ${open}`} />
                <div style={{ height: h(click) }}  className="bg-cyan-400/40 transition-all"    title={`Clicked ${click}`} />
                <div style={{ height: h(bounce) }} className="bg-red-400/50 transition-all"     title={`Bounced/failed ${bounce}`} />
              </div>
              <p className="text-[9px] text-white/40 group-hover:text-white/70 transition-colors">
                {d.slice(5)}
              </p>
            </div>
          );
        })}
      </div>
      <div className="flex flex-wrap gap-4 mt-4 text-[10px] text-white/45">
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-400/40 inline-block"/>Delivered</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-sky-400/40 inline-block"/>Opened</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-cyan-400/40 inline-block"/>Clicked</span>
        <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-red-400/50 inline-block"/>Bounced / failed</span>
      </div>
    </div>
  );
}

function EmailTimelineModal({ emailId, onClose }) {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/admin/email-events/by-email/${encodeURIComponent(emailId)}`)
      .then(({ data }) => { if (active) setEvents(data.events || []); })
      .catch(() => { if (active) setError("Could not load timeline."); });
    return () => { active = false; };
  }, [emailId]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="email-timeline-modal"
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className="bg-[#0A0A0A] border border-white/[0.12] w-full max-w-2xl max-h-[80vh] overflow-y-auto"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/[0.10]">
          <div>
            <p className="text-[10px] tracking-[0.15em] uppercase text-white/45">Email timeline</p>
            <p className="text-white/80 text-xs font-mono mt-1">{emailId}</p>
          </div>
          <button
            data-testid="email-timeline-close"
            type="button"
            onClick={onClose}
            className="text-white/60 hover:text-white"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          {error && <p className="text-red-300 text-xs">{error}</p>}
          {!events && !error && <p className="text-white/45 text-xs">Loading…</p>}
          {events && events.length === 0 && <p className="text-white/45 text-xs">No events.</p>}
          {events && events.map((e, idx) => (
            <div key={idx} className="border-l-2 border-white/10 pl-4 py-2">
              <div className="flex items-center gap-3">
                {eventChip(e.event_type)}
                <span className="text-white/40 text-[10px] font-mono">
                  {new Date(e.created_at).toLocaleString()}
                </span>
              </div>
              {e.error && (
                <p className="text-red-300 text-[11px] mt-1">{e.error}</p>
              )}
              {e.raw && (
                <details className="mt-2">
                  <summary className="text-white/30 text-[10px] cursor-pointer hover:text-white/60">Raw payload</summary>
                  <pre className="text-[10px] text-white/50 bg-black/40 p-2 mt-1 overflow-x-auto">
                    {JSON.stringify(e.raw, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Health
// =============================================================================

function HealthTab() {
  const [health, setHealth] = useState(null);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fxRefreshing, setFxRefreshing] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [h, e] = await Promise.all([api.get("/admin/health"), api.get("/admin/recent-errors")]);
      setHealth(h.data);
      setErrors(e.data.errors || []);
    } catch (e) {
      toast.error("Failed to load health");
    } finally {
      setLoading(false);
    }
  };

  const refreshFx = async () => {
    setFxRefreshing(true);
    try {
      const r = await api.post("/admin/fx/refresh");
      const stamp = (r.data?.updated_at || "").slice(11, 19);
      toast.success(stamp ? `FX cache refreshed · ${stamp} UTC` : "FX cache refreshed");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FX refresh failed");
    } finally {
      setFxRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading || !health) {
    return <div className="text-white/60 text-sm py-12 text-center">Loading…</div>;
  }

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button
          onClick={load}
          data-testid="admin-health-refresh"
          className="text-white/65 hover:text-[#D4AF37] transition-colors inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase"
        >
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <HealthCard
          title="MongoDB Atlas"
          icon={Database}
          status={health.mongodb?.status === "ok" ? "ok" : "error"}
          detail={
            health.mongodb?.status === "ok"
              ? `Latency: ${health.mongodb.latency_ms} ms`
              : health.mongodb?.error || "Unknown error"
          }
        />
        <HealthCard
          title="FX Rates"
          icon={RefreshCw}
          status={health.fx_rates?.status === "ok" ? "ok" : health.fx_rates?.status === "stale" ? "warn" : "error"}
          detail={
            health.fx_rates?.last_fetch
              ? `Last fetch: ${health.fx_rates.last_fetch.slice(0, 10)} (${health.fx_rates.age_hours}h ago)`
              : "Never fetched"
          }
          action={
            <button
              onClick={refreshFx}
              disabled={fxRefreshing}
              data-testid="admin-fx-force-refresh"
              className="inline-flex items-center gap-1.5 text-[10px] tracking-wider uppercase text-[#D4AF37] hover:text-[#D4AF37]/80 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              <RefreshCw className={`w-3 h-3 ${fxRefreshing ? "animate-spin" : ""}`} />
              {fxRefreshing ? "Refreshing…" : "Force refresh now"}
            </button>
          }
        />
        <HealthCard
          title="Sub-processors"
          icon={Sparkles}
          status="ok"
          detail={
            [
              `LLM: ${health.llm_configured ? "✓" : "—"}`,
              `Resend: ${health.resend_configured ? "✓" : "—"}`,
              `R2: ${health.r2_configured ? "✓" : "—"}`,
              `Sentry: ${health.sentry_configured ? "✓" : "—"}`,
              `Admin email: ${health.admin_email_configured ? "✓" : "—"}`,
            ].join("  ·  ")
          }
        />
        <HealthCard
          title="Environment"
          icon={Activity}
          status={health.app_env === "production" ? "ok" : "warn"}
          detail={`APP_ENV = ${health.app_env}${health.app_env === "production" ? "" : "  (set to 'production' before deploy)"}`}
        />
        <HealthCard
          title="Last Visa Reminder Sent"
          icon={Mail}
          status="ok"
          detail={health.last_visa_reminder_sent ? `${health.last_visa_reminder_sent.slice(0, 19).replace("T", " ")}` : "No reminders sent yet"}
        />
        <HealthCard
          title="Last Payment Reminder Sent"
          icon={Mail}
          status="ok"
          detail={health.last_payment_reminder_sent ? `${health.last_payment_reminder_sent.slice(0, 19).replace("T", " ")}` : "No reminders sent yet"}
        />
      </div>

      {/* Recent errors */}
      <div>
        <p className="text-[10px] tracking-[0.2em] uppercase text-white/60 mb-3 flex items-center gap-2">
          <AlertOctagon className="w-3.5 h-3.5 text-red-400" /> Recent Errors ({errors.length})
        </p>
        {errors.length === 0 ? (
          <div className="bg-emerald-500/[0.03] border border-emerald-500/15 p-4">
            <p className="text-emerald-200/80 text-xs flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" /> No recent errors. System healthy.
            </p>
          </div>
        ) : (
          <div className="bg-white/[0.02] border border-white/[0.10] p-2 max-h-[400px] overflow-auto font-mono text-[11px] space-y-1">
            {errors.map((line, i) => (
              <div key={i} className="text-red-200/70 leading-relaxed break-all">{line}</div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function StatCard({ label, value, sub, highlight }) {
  return (
    <div className={`p-4 border ${highlight ? "border-[#D4AF37]/25 bg-[#D4AF37]/[0.02]" : "border-white/[0.10] bg-white/[0.01]"}`}>
      <p className="text-[9px] tracking-wider uppercase text-white/55 mb-1">{label}</p>
      <p className="text-white text-2xl tabular-nums font-serif">{value ?? "—"}</p>
      {sub && <p className="text-white/55 text-[10px] mt-0.5">{sub}</p>}
    </div>
  );
}

function HealthCard({ title, icon: Icon, status, detail, action }) {
  const color =
    status === "ok" ? "border-emerald-500/15 text-emerald-300" :
    status === "warn" ? "border-amber-500/15 text-amber-300" :
    "border-red-500/15 text-red-300";
  const dot =
    status === "ok" ? "bg-emerald-400" :
    status === "warn" ? "bg-amber-400" :
    "bg-red-400";
  return (
    <div className={`bg-white/[0.02] border ${color} p-4`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5 text-white/85" strokeWidth={1.6} />}
          <p className="text-white text-sm font-medium">{title}</p>
        </div>
        <span className={`w-2 h-2 rounded-full ${dot}`} />
      </div>
      <p className="text-white/85 text-[11px] leading-relaxed">{detail}</p>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}
